// team/shared/components/notification/TeamNotificationSnackbar.js
import React from 'react';
import { Snackbar, Alert, IconButton } from '@mui/material';
import { CloseIcon, CheckCircleIcon, ErrorIcon, InfoIcon, WarningIcon } from 'icons/materialIcons';

const TeamNotificationSnackbar = ({
    open,
    message,
    handleClose,
    duration = 3000, // Reduced from 5000 to 3000
    severity = 'success', // Default severity is success
    anchorOrigin = { vertical: 'top', horizontal: 'center' }
}) => {
    // Filter out certain types of messages to reduce notification spam
    const shouldShowNotification = (message, severity) => {
        // Don't show technical error messages
        if (message?.includes('timeout') ||
            message?.includes('network') ||
            message?.includes('connection') ||
            message?.includes('Failed to fetch') ||
            message?.includes('ECONNREFUSED') ||
            message?.includes('ENOTFOUND')) {
            console.log('Suppressing technical notification:', message);
            return false;
        }

        // Don't show generic error messages
        if (message?.includes('An unexpected error occurred') ||
            message?.includes('Something went wrong')) {
            console.log('Suppressing generic notification:', message);
            return false;
        }

        // Don't show server error messages
        if (message?.includes('Server error') ||
            message?.includes('500') ||
            message?.includes('502') ||
            message?.includes('503') ||
            message?.includes('504')) {
            console.log('Suppressing server error notification:', message);
            return false;
        }

        return true;
    };

    // Check if notification should be shown
    const shouldShow = shouldShowNotification(message, severity);

    // Map of severity to their corresponding icons
    const severityIcons = {
        success: <CheckCircleIcon />,
        error: <ErrorIcon />,
        info: <InfoIcon />,
        warning: <WarningIcon />
    };

    // Get the appropriate icon based on severity
    const icon = severityIcons[severity] || severityIcons.success;

    // Don't render if notification should be suppressed
    if (!shouldShow) {
        return null;
    }

    return (
        <Snackbar
            open={open}
            autoHideDuration={duration === 0 ? null : Math.min(duration, 4000)} // Cap at 4 seconds max, or null if duration is 0
            onClose={handleClose}
            anchorOrigin={anchorOrigin}
        >
            <Alert
                onClose={handleClose}
                severity={severity}
                variant="filled"
                sx={{
                    padding: '10px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    // Different background colors based on severity
                    backgroundColor: theme => {
                        switch (severity) {
                            case 'error':
                                return theme.palette.error.main;
                            case 'warning':
                                return theme.palette.warning.main;
                            case 'info':
                                return theme.palette.info.main;
                            case 'success':
                            default:
                                return theme.palette.success.main;
                        }
                    }
                }}
                icon={icon}
            >
                {message}
            </Alert>
        </Snackbar>
    );
};

export default TeamNotificationSnackbar;